import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "@metacrm/metacrm-material-ui/dist/Button";
import InputField from "@metacrm/metacrm-material-ui/dist/InputField";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import axios from "axios";
import { apiUrl } from "features/configure";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useDispatch } from "react-redux";
import { postRegisterVerifyAsyncAction } from "features/metadesk/redux/trial/trial.action";
import { useConnectWallet } from "features/home/redux/connectWallet";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import Slider from "react-slick";
import { useOnboardingState } from "../OnboardingProvider";
import { useIntl } from "react-intl";

const roleToModuleMap = {
  "Social Media": "community",
  Marketing: "community",
  "Customer Success": "community",
  "Freelancer/Consultant": "community",
  Other: "community",
  "Business Development": "footprint",
  Sales: "footprint",
  "Data Analysis": "footprint",
};

export default function TrialStep3({ nextStep, stepData, handleStepData }) {
  const {
    handleSubmit,
    control,
    setError, // 引入 setError 方法來手動設置錯誤
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      workSpaceName: stepData.workSpaceName || "",
      module: stepData.module || "",
      telegramId: stepData.telegramId || "",
    },
  });

  useEffect(() => {
    handleStepData(
      "module",
      stepData?.role ? roleToModuleMap[stepData.role] : "community"
    );
  }, []);

  const dispatchSnackbar = useDispatchSnackbar();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const { getAuthenticatedInfo } = useConnectWallet();
  const { fetchEntityConfig } = useFetchEntityConfig();
  const { ecosystem, address } = useOnboardingState();

  const handleVerify = async () => {
    const { data } = await axios.post(`${apiUrl}/api/entity/register`, {
      registerId: stepData.registerId,
      workspaceName: stepData.workSpaceName,
      address,
      module: stepData.module,
      ecosystem: ecosystem?.toLowerCase(),
      telegramId: stepData.telegramId,
    });
    await dispatch(postRegisterVerifyAsyncAction(data));
    await getAuthenticatedInfo(stepData.workSpaceName);
    fetchEntityConfig({ entityName: stepData.workSpaceName });
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await handleVerify();
      nextStep();
    } catch (err) {
      if (err.response && err.response.status === 409) {
        // 設置工作區名稱的錯誤訊息
        setError("workSpaceName", {
          type: "manual",
          message: formatMessage({ id: "global.error.workspaceNameTaken" }),
        });
      } else {
        // 如果是其他錯誤類型，使用 snackbar 來顯示
        dispatchSnackbar({ err });
      }
    } finally {
      setIsLoading(false);
    }
  };
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const modulesOption = [
    {
      type: "community",
      name: (
        <Box className="item-flex">
          {formatMessage(
            { id: "global.focusOption.communityCdp" },
            {
              img1: (
                <img
                  src={require("assets/img/onboarding/communityIcon.png")}
                  style={{ marginRight: "5px" }}
                />
              ),
              img2: (
                <img
                  src={require("assets/img/onboarding/cdpIcon.png")}
                  style={{ margin: "0 5px" }}
                />
              ),
            }
          )}
        </Box>
      ),
      feature: [
        formatMessage({ id: "global.focusOption.communityCdp.feature1" }),
        formatMessage({ id: "global.focusOption.communityCdp.feature2" }),
        formatMessage({ id: "global.focusOption.communityCdp.feature3" }),
        formatMessage({ id: "global.focusOption.communityCdp.feature4" }),
      ],
    },
    {
      type: "footprint",
      name: (
        <Box className="item-flex">
          {formatMessage(
            { id: "global.focusOption.footprintsCdp" },
            {
              img1: (
                <img
                  src={require("assets/img/onboarding/footprintIcon.png")}
                  style={{ marginRight: "5px" }}
                />
              ),
              img2: (
                <img
                  src={require("assets/img/onboarding/cdpIcon.png")}
                  style={{ margin: "0 5px" }}
                />
              ),
            }
          )}
        </Box>
      ),
      feature: [
        formatMessage({ id: "global.focusOption.footprintsCdp.feature1" }),
        formatMessage({ id: "global.focusOption.footprintsCdp.feature2" }),
        formatMessage({ id: "global.focusOption.footprintsCdp.feature3" }),
      ],
    },
  ];

  const demoPictures = {
    community: [
      {
        src: require("assets/img/onboarding/communityChart.png"),
        title: formatMessage({ id: "global.module.community" }),
        description: formatMessage({
          id: "global.module.community.description",
        }),
      },
    ],
    footprint: [
      {
        src: require("assets/img/onboarding/footprintChart.png"),
        title: formatMessage({ id: "global.module.footprints" }),
        description: formatMessage({
          id: "global.module.footprint.description",
        }),
      },
    ],
  };
  const theme = useTheme();
  const computer = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
      }}
      gap="88px"
    >
      <Box>
        <Box color="#383538" fontSize="24px" fontWeight={700} mb="8px">
          {formatMessage({ id: "global.setupReady.title" })}
        </Box>
        <Box color="#777" fontSize="14px" mb="32px">
          {formatMessage({ id: "global.setupReady.subtitle" })}
        </Box>
        <Box width="100%" color="#555" mb="8px" fontSize="16px">
          {formatMessage({ id: "global.focusSelection.prompt" })} :
        </Box>
        {modulesOption.map((item, index) => {
          const active = stepData.module === item.type;
          return (
            <Box
              textAlign="left"
              width="100%"
              padding={active ? "25px 18px" : "12px 18px"}
              borderRadius="4px"
              border={active ? "1px solid #7B61FF" : "1px solid #E6E6E6"}
              backgroundColor={active ? "#FAF6FF" : "#FFF"}
              mb="8.3px"
              sx={{
                cursor: "pointer",
                ...(active && { transition: "all 0.5s ease" }),
              }}
              onClick={() => {
                handleStepData("module", item.type);
              }}
            >
              <Box color="#777" fontSize="16px" fontWeight={700}>
                {item.name}
              </Box>
              {active && (
                <Box mt="26.5px">
                  {item.feature.map((feature, index) => (
                    <Box key={index} className="item-flex" mb="8px">
                      <i
                        className="meta-crm-icon-ic_check font-size-20"
                        style={{ color: "#7B61FF" }}
                      />
                      <Box color="#777" ml="4px">
                        {feature}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}

        {/* 使用 Controller 來包裝 InputField */}
        <Box sx={{ width: "100%", mt: "24px" }}>
          <Controller
            name="workSpaceName"
            control={control}
            rules={{
              required: "This field is required.",
              pattern: {
                value: /^[A-Za-z0-9]+$/,
                message: "Alphanumeric characters only.",
              },
            }}
            render={({ field }) => (
              <InputField
                {...field}
                title={formatMessage({ id: "global.form.workspaceName.label" })}
                isRequired={true}
                placeholder="e.g. MetaCRM"
                errors={errors.workSpaceName?.message}
                onChange={(e) => {
                  field.onChange(e);
                  handleStepData("workSpaceName", e.target.value);
                }}
              />
            )}
          />
        </Box>
        <Box height="16px" />
        <Button
          onClick={handleSubmit(onSubmit)}
          sx={{ mt: "24.75px" }}
          isLoading={isLoading}
        >
          {formatMessage({ id: "global.btn.create" })}
        </Button>
      </Box>
      {computer && (
        <Box
          sx={{
            height: "437px",
            width: "715px",
            maxWidth: "100vw",
            mt: "50px",
          }}
        >
          <Slider {...settings}>
            {demoPictures[stepData?.module]?.map((item, index) => (
              <Box
                borderRadius="7px"
                border="1px solid #E6E6E6"
                background="#FAFAFD"
                p="36px 28px 0"
                color="#777"
                fontSize={12}
              >
                <Box fontWeight={700} mb="8px">
                  {item.title}
                </Box>
                <Box fontWeight={400} mb="16px">
                  {item.description}
                </Box>
                <Box
                  component="img"
                  sx={{ height: "auto", width: "100%" }}
                  src={item.src}
                  alt="Demo"
                />
              </Box>
            ))}
          </Slider>
        </Box>
      )}
    </Box>
  );
}
